import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import toast from 'react-hot-toast';
import { postRequest } from '../../../lib/api';
import { useContext } from 'react';
import { AuthContext } from '../../../authContext/context';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';

export default function EditBranch() {
  const { id } = useParams();
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const [loading, setLoading] = React.useState(false);
  const [initialLoading, setInitialLoading] = React.useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  // Fetch branch data using the new API
  React.useEffect(() => {
    const fetchBranchData = async () => {
      try {
        const response = await postRequest(
          'private/getSingleBranchData',
          { branch_id: id },
          authCtx.token
        );
        if (response?.status === 200) {
          const branch = response?.data;
          reset({
            name: branch.name || '',
            address1: branch.address?.address1 || '',
            address2: branch.address?.address2 || '',
            town: branch.address?.town || '',
            country: branch.address?.country || '',
            postcode: branch.address?.postcode || '',
          });
        } else {
          toast.error(response?.message || 'Failed to fetch branch data');
        }
      } catch (err) {
        toast.error(err?.message || 'Error loading branch');
      } finally {
        setInitialLoading(false);
      }
    };

    if (id) {
      fetchBranchData();
    }
  }, [id, authCtx.token, reset]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        name: data?.name,
        address: {
          address1: data?.address1,
          address2: data?.address2,
          town: data?.town,
          country: data?.country,
          postcode: data?.postcode,
        },
      };

      const response = await postRequest(`private/editBranch/${id}`, payload, authCtx.token);
      if (response?.status === 200) {
        toast.success('Branch updated successfully!');
        reset();
        navigate('/branches');
      } else {
        toast.error(response?.message || 'Branch update failed');
      }
    } catch (err) {
      toast.error(err?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Branches', path: '/branches' },
          { label: 'Edit Branch', path: '' },
        ]}
      />
      <br />
      <div className="page-header">
        <h1 className="mt-0">Edit Branch</h1>
      </div>

      <div className="page-top-formview mb-3">
        {initialLoading ? (
          <div className="text-center"><CircularProgress /></div>
        ) : (
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row className="mb-3">
              <Form.Group as={Col} controlId="title">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Name"
                  {...register('name', { required: 'Name is required' })}
                  isInvalid={!!errors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>

            <Form.Label>Address</Form.Label>
            <Row className="mb-3">
              <Form.Group as={Col} controlId="line1">
                <Form.Control
                  type="text"
                  placeholder="Line 1"
                  {...register('address1', { required: 'Address Line 1 is required' })}
                  isInvalid={!!errors.address1}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.address1?.message}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group as={Col} controlId="line2">
                <Form.Control type="text" placeholder="Line 2" {...register('address2')} />
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} controlId="town">
                <Form.Control
                  type="text"
                  placeholder="Town"
                  {...register('town', { required: 'Town is required' })}
                  isInvalid={!!errors.town}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.town?.message}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group as={Col} controlId="county">
                <Form.Control
                  type="text"
                  placeholder="County"
                  {...register('country', { required: 'Country is required' })}
                  isInvalid={!!errors.country}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.country?.message}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group as={Col} controlId="postcode">
                <Form.Control
                  type="text"
                  placeholder="Postcode"
                  {...register('postcode', {
                    required: 'Postcode is required',
                    pattern: {
                      value: /^[A-Za-z0-9 ]{3,10}$/,
                      message: 'Enter a valid postcode',
                    },
                  })}
                  isInvalid={!!errors.postcode}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.postcode?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>

            <Stack direction="row" spacing={2}>
              <Button variant="outlined" color="inherit" type="button" onClick={() => navigate('/branches')}>
                Cancel
              </Button>
              <Button variant="contained" className="red-bg red-button text-capitalize" type="submit" disabled={loading}>
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Submit'}
              </Button>
            </Stack>
          </Form>
        )}
      </div>
    </>
  );
}
